import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { TreeVideoDto } from '@lib/dto/tree-video.dto'
import { videoOwnerId, type VideoOwner } from '@lib/input/tree-video.input'
import { MAX_VIDEO_SECONDS } from '@lib/constants/documents'
import { treeVideoService } from '@/services/TreeVideoService'
import { treeVideoKeys } from '@/hooks/queryKeys'

/** How often to re-ask while anything is still transcoding. */
const PROCESSING_POLL_MS = 5_000

/**
 * Whether this deployment can take video at all.
 *
 * Asked once and kept, because it is a property of the deployment rather than
 * of any request — it can only change when the API is redeployed with different
 * configuration, at which point the page has been reloaded anyway.
 */
export function useVideoAvailability(): UseQueryResult<boolean> {
  return useQuery({
    queryKey: treeVideoKeys.availability(),
    queryFn: async () => (await treeVideoService.availability()).enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  })
}

/**
 * Videos of one tree or irrigation record.
 *
 * Polls while anything is unsettled and stops once everything has landed.
 * Stream transcodes asynchronously and there is no webhook, so this is how a
 * tile stops saying "Processing" — and it costs nothing in the normal case,
 * where every video is already READY and the interval is switched off.
 */
export function useVideos(
  owner: VideoOwner,
  /** `visitId`: for a tree, only that visit's; null, every visit's. */
  options: { enabled?: boolean; visitId?: string | null } = {},
): UseQueryResult<TreeVideoDto[]> {
  const visitId = options.visitId ?? null
  return useQuery({
    queryKey: treeVideoKeys.list(owner, visitId),
    queryFn: async () => (await treeVideoService.list(owner, visitId)).videos,
    // An empty id is a sheet that has not settled yet, not something to fetch.
    // `videoOwnerId` rather than `Object.values`, which is `any[]` on a union.
    enabled: (options.enabled ?? true) && videoOwnerId(owner) !== '',
    refetchInterval: (query) => {
      const settled = (query.state.data ?? []).every(
        (video) => video.status === 'READY' || video.status === 'ERRORED',
      )
      return settled ? false : PROCESSING_POLL_MS
    },
  })
}

/**
 * Read a video's duration without uploading it.
 *
 * Costs one metadata load and saves a surveyor on a rural connection sending
 * ten minutes of footage that Stream would reject at the far end. Resolves null
 * when the browser cannot work it out, in which case the API and Stream still
 * enforce the limit.
 */
/** Give up on metadata that never arrives, so the object URL is released. */
const METADATA_TIMEOUT_MS = 10_000

async function readDurationSeconds(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.preload = 'metadata'
    let settled = false
    const done = (value: number | null): void => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      URL.revokeObjectURL(url)
      video.onloadedmetadata = null
      video.onerror = null
      video.removeAttribute('src')
      video.load()
      resolve(value)
    }
    const timer = window.setTimeout(() => {
      done(null)
    }, METADATA_TIMEOUT_MS)
    video.onloadedmetadata = () => {
      done(Number.isFinite(video.duration) ? Math.round(video.duration) : null)
    }
    video.onerror = () => {
      done(null)
    }
    video.src = url
  })
}

/**
 * One video by id, polled while it is still transcoding.
 *
 * How a stock clip is read: it belongs to no tree, so there is no list to find
 * it in. Same polling rule as the list — Stream transcodes asynchronously with
 * no webhook, so this is what lets the tile stop saying "Processing", and it
 * costs nothing once the clip has landed.
 */
export function useVideo(
  videoId: string | null,
): UseQueryResult<TreeVideoDto> {
  return useQuery({
    queryKey: treeVideoKeys.detail(videoId ?? ''),
    queryFn: () => treeVideoService.get(videoId ?? ''),
    enabled: videoId !== null,
    refetchInterval: (query) => {
      const video = query.state.data
      if (video === undefined) return false
      const settled = video.status === 'READY' || video.status === 'ERRORED'
      return settled ? false : PROCESSING_POLL_MS
    },
  })
}

export function useUploadVideo(
  /** Null uploads a stock clip for the problem directory. */
  owner: VideoOwner | null,
  onProgress?: (percent: number | null) => void,
  /** For a tree, the visit it is taken on; left out, the site's current. */
  visitId?: string | null,
): UseMutationResult<TreeVideoDto, Error, File> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      if (file.size === 0) {
        throw new Error('That video is empty.')
      }
      const durationSeconds = await readDurationSeconds(file)
      if (durationSeconds !== null && durationSeconds > MAX_VIDEO_SECONDS) {
        throw new Error(
          `Videos must be ${String(Math.floor(MAX_VIDEO_SECONDS / 60))} minutes or shorter.`,
        )
      }
      return treeVideoService.upload(
        owner,
        file,
        {
          fileName: file.name,
          ...(durationSeconds === null ? {} : { durationSeconds }),
          ...(visitId === undefined ? {} : { visitId }),
        },
        onProgress
          ? { onProgress: (p) => { onProgress(p.percent) } }
          : {},
      )
    },
    onSuccess: () => {
      // A stock clip has no list to refresh; the directory that now points at
      // it is invalidated by whoever stored the id.
      if (owner !== null) {
        void queryClient.invalidateQueries({
          queryKey: treeVideoKeys.owner(owner),
        })
      }
    },
  })
}

export function useArchiveVideo(
  /** Null for a stock clip, which has no list to refresh. */
  owner: VideoOwner | null,
): UseMutationResult<TreeVideoDto, Error, string> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => treeVideoService.archive(id),
    onSuccess: (_video, id) => {
      void queryClient.invalidateQueries({ queryKey: treeVideoKeys.detail(id) })
      if (owner !== null) {
        void queryClient.invalidateQueries({
          queryKey: treeVideoKeys.owner(owner),
        })
      }
    },
  })
}
